import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationsStore } from '../store/notificationsStore';
import { Notification } from '../services/notificationsApi';
import styles from './NotificationDropdown.module.css';

/**
 * Header notification dropdown.
 *
 * Pulls the latest notifications via the notifications store. Clicking a
 * notification marks it read; "Mark all as read" hits PATCH /notifications/read-all;
 * the unread count drives the badge on the bell icon (handled by the parent Header).
 */

interface Props {
  open: boolean;
  onClose: () => void;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function targetPath(n: Notification): string {
  // Best-effort routing based on notification type / related_type.
  switch (n.type) {
    case 'order':    return '/orders';
    case 'payment':  return '/payments';
    case 'delivery': return '/customer-deliveries';
    case 'inventory': return '/inventory';
    case 'support':  return '/support';
    default:          return '/notifications';
  }
}

export const NotificationDropdown: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const items = useNotificationsStore((s) => s.items);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const fetchList = useNotificationsStore((s) => s.fetchList);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const ref = useRef<HTMLDivElement>(null);

  // Refresh when opening so the list is current.
  useEffect(() => {
    if (open) fetchList({ limit: 25 });
  }, [open, fetchList]);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onClose]);

  if (!open) return null;

  const handleClick = (n: Notification) => {
    if (!n.is_read) markRead(n.id).catch(() => undefined);
    navigate(targetPath(n));
    onClose();
  };

  const recent = items.slice(0, 25);

  return (
    <div ref={ref} className={styles.dropdown}>
      <header className={styles.header}>
        <span className={styles.title}>Notifications</span>
        {unreadCount > 0 && (
          <button
            type="button"
            className={styles.markAllBtn}
            onClick={() => markAllRead().catch(() => undefined)}
          >
            Mark all as read
          </button>
        )}
      </header>
      <div className={styles.list}>
        {recent.length === 0 && (
          <div className={styles.empty}>You're all caught up.</div>
        )}
        {recent.map((n) => (
          <button
            key={n.id}
            type="button"
            className={`${styles.item} ${!n.is_read ? styles.unread : ''}`}
            onClick={() => handleClick(n)}
          >
            <div className={styles.itemHeader}>
              <span className={styles.itemTitle}>{n.title}</span>
              <span className={styles.itemTime}>{relativeTime(n.created_at)}</span>
            </div>
            <p className={styles.itemBody}>{n.message}</p>
          </button>
        ))}
      </div>
      <footer className={styles.footer}>
        <button
          type="button"
          className={styles.viewAllBtn}
          onClick={() => { navigate('/notifications'); onClose(); }}
        >
          View all notifications
        </button>
      </footer>
    </div>
  );
};

export default NotificationDropdown;
